import { motion } from "motion/react";
import { Phone, Mail, MessageCircle } from "lucide-react";
import { SectionLabel } from "@/components/common/SectionLabel";
import { AnimatedTicker } from "@/components/common/AnimatedTicker";
import { CONTACT_INFO } from "@/constants/company";
import { ANIMATION_VARIANTS } from "@/constants/animations";

export function ContactSection() {
  const contactCards = [
    {
      label: "Informes",
      value: CONTACT_INFO.phones.informes,
      icon: Phone,
    },
    {
      label: "Ventas",
      value: CONTACT_INFO.phones.ventas,
      icon: Phone,
    },
    {
      label: "Correo",
      value: CONTACT_INFO.email,
      icon: Mail,
    },
  ];

  return (
    <section
      id="contacto"
      className="py-[120px] bg-gradient-to-br from-[#edf8f8] via-[#f0f9f9] to-[#A5E0DF]/15 border-y border-[#A5E0DF]/30 overflow-hidden"
    >
      <div className="max-w-5xl mx-auto px-6">
        {/* Header */}
        <motion.div
          variants={ANIMATION_VARIANTS.fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-80px" }}
          className="text-center mb-12"
        >
          <SectionLabel text="Inicie su Proyecto" />
          <h2 className="text-4xl md:text-5xl font-bold text-[#0A2540] max-w-3xl mx-auto leading-snug mt-2">
            Hablemos de su{" "}
            <span className="text-[#39B5BF]">próximo proyecto.</span>
          </h2>
          <p className="mt-8 text-[16px] md:text-[18px] text-[#0A2540]/75 max-w-3xl mx-auto font-normal leading-[1.7]">
            Envíenos su solicitud técnica y nuestro equipo de especialistas
            preparará una propuesta personalizada sin costo. Puede incluir en su
            mensaje: área en m², número de usuarios, tipo de instalación, planos
            o croquis disponibles, plazo de ejecución requerido y normativas a
            cumplir.
          </p>
        </motion.div>

        {/* Animated Ticker */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="border-y border-[#39B5BF]/25 bg-white/60 rounded-lg shadow-sm"
        >
          <div className="px-5 pt-4 pb-1">
            <span className="text-[#1A3F75] text-[12px] font-bold uppercase tracking-widest">
              Podría consultar sobre:
            </span>
          </div>
          <AnimatedTicker />
        </motion.div>

        {/* CTA Buttons */}
        <motion.div
          variants={ANIMATION_VARIANTS.fadeUp}
          custom={0.4}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="mt-14 flex flex-col sm:flex-row items-center justify-center gap-6"
        >
          <a
            href={`https://wa.me/${CONTACT_INFO.whatsapp.number}?text=Hola,%20deseo%20solicitar%20una%20propuesta%20técnica`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-2 bg-[#25D366] text-white px-10 py-4 rounded-lg font-bold text-[15px] hover:bg-[#20B45B] transition-all hover:shadow-xl shadow-lg"
          >
            <MessageCircle className="w-5 h-5" />
            WhatsApp Directo
          </a>
        </motion.div>

        {/* Contact Cards */}
        <motion.div
          variants={ANIMATION_VARIANTS.staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-8 text-center"
        >
          {contactCards.map(({ label, value, icon: Icon }) => (
            <motion.div
              key={label}
              variants={ANIMATION_VARIANTS.cardVariant}
              className="bg-white border border-[#A5E0DF]/40 rounded-lg p-6 flex flex-col items-center gap-4 hover:border-[#39B5BF]/60 hover:shadow-md transition-all group"
            >
              <div className="w-12 h-12 bg-[#39B5BF]/10 border border-[#39B5BF]/20 rounded-full flex items-center justify-center group-hover:bg-[#39B5BF]/20 transition-colors">
                <Icon className="w-5 h-5 text-[#39B5BF]" strokeWidth={2} />
              </div>
              <div>
                <div className="text-[#1A3F75] text-xs font-bold uppercase tracking-widest">
                  {label}
                </div>
                <div className="font-mono font-bold text-[#0A2540] text-[16px] mt-1.5">
                  {value}
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}